import { StandardTabsV2 } from '@cimplico/components';
import { useMemo, useEffect } from 'react';
import { WorksheetChecklistItemStatusEnum } from '../../../types/worksheet-checklist-item';
import { GenericWorksheet } from '../../../types/worksheet';
import { useSearchParams } from 'react-router-dom';
import { SparklesIcon } from '@heroicons/react/20/solid';

type ContentTabsProps = {
    onChange: (content: string) => void;
    worksheet: GenericWorksheet;
};

function ContentTabs({ onChange, worksheet }: ContentTabsProps): JSX.Element {
    /**
     * The tabs shown to the user depends on the content of the current worksheet.
     */
    const availableTabs = useMemo(() => {
        const entriesCount = worksheet.entries.length;
        const calculationsCount = worksheet.calculations.length;
        const checklistCount = worksheet.checklist.length;
        const completedChecklistCount = worksheet.checklist.filter((c) => c.status !== WorksheetChecklistItemStatusEnum.UNCHECKED).length;

        const hasChecklist = worksheet.checklist.length > 0;

        const _tabs: Record<string, string>[] = [{ label: `Entries (${entriesCount})`, value: 'entries' }];

        _tabs.push({ label: `Calculations (${calculationsCount})`, value: 'calculations' });

        if (hasChecklist) {
            _tabs.push({ label: `Checklist (${completedChecklistCount}/${checklistCount})`, value: 'checklist' });
        }

        if (worksheet.account && worksheet.journals) {
            _tabs.push({ label: `Journals (${worksheet.journals.length})`, value: 'journals' });
        }

        _tabs.push({ label: `Files (${worksheet.files.length})`, value: 'files' }, { label: 'History', value: 'history' });
        return _tabs;
    }, [worksheet]);

    const tabLabels = availableTabs.map((t) => ({ label: t.label }));
    const tabValues = availableTabs.map((t) => t.value);

    const [search, setSearch] = useSearchParams();
    const index = Math.max(tabValues.indexOf(search.get('tab') || ''), 0);

    function handleChange(idx: number): void {
        const val = tabValues[idx];
        setSearch(
            (s) => {
                s.set('tab', val);
                return s;
            },
            { replace: true }
        );
        onChange(tabValues[index]);
    }

    useEffect(() => {
        handleChange(index);
    }, [index]);

    return (
        <div data-intercom-target="generic-worksheet-tabs" className="border-b">
            <StandardTabsV2 selectedIndex={index} onChange={handleChange} name="content">
                {tabLabels.map((t, i) => {
                    return (
                        <StandardTabsV2.Tab key={i} label={t.label} badgeColor="indigo" badgeRounded={false} badgeIcon={<SparklesIcon />} />
                    );
                })}
            </StandardTabsV2>
        </div>
    );
}

export default ContentTabs;
